/**
 * MappingOverviewPanel — Header + search + Table Navigator + unified
 * Review pane (Issue #656).
 *
 * The legacy SUMMARY block and REVIEW QUEUE were removed; their
 * counts/lists are now produced by `useReviewItems` upstream and
 * rendered through `ReviewPane`. This component is responsible for
 * navigation controls (search, table-list focus controls, drill-in/out)
 * and for hosting the Review pane.
 */
import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  AccountTree as PipelineIcon,
} from '@mui/icons-material';

import type { FieldMapping, MappingResponse } from '../services/api';
import type { CanvasGraph } from './types';
import ReviewPane, { type ReviewBucketTab } from './review/ReviewPane';
import type { ReviewItem, ReviewItemCounts } from './hooks/useReviewItems';
import type { FieldSelection } from './state/useFieldSelection';

// Used only by the per-table progress bar in the Tables Navigator.
const CONF_HIGH = 0.9;
const CONF_MED = 0.7;
const COLOR_HIGH = '#10b981';
const COLOR_MED = '#f59e0b';
const COLOR_LOW = '#ef4444';

function confidenceColor(c: number): string {
  if (c >= CONF_HIGH) return COLOR_HIGH;
  if (c >= CONF_MED) return COLOR_MED;
  return COLOR_LOW;
}

// ─── Table-level stats ──────────────────────────────────────────────────
interface TableStat {
  id: string;
  label: string;
  role: 'source' | 'target';
  totalFields: number;
  mappedFields: number;
  unmappedFields: number;
  avgConfidence: number;
}

// ─── Props ──────────────────────────────────────────────────────────────
export interface MappingOverviewPanelProps {
  mappingResponse?: MappingResponse;
  canvasGraph?: CanvasGraph;
  // Table focus / filter
  focusedTargetIds: Set<string>;
  focusedSourceIds: Set<string>;
  onToggleTarget: (id: string) => void;
  onToggleSource: (id: string) => void;
  onClearFocus?: () => void;
  availableGroups: Array<{ id: string; label: string; role: 'source' | 'target' }>;
  // Review queue navigation
  onFocusNode: (nodeId: string) => void;
  onSelectEdge: (edgeId: string) => void;
  // View mode
  canvasViewMode?: 'canvas' | 'mapping-table';
  // Drill-down (overview ↔ detail)
  canvasSubState?: 'overview' | 'detail';
  detailTableId?: string | null;
  detailTableRole?: 'source' | 'target' | null;
  onDrillIntoTable?: (tableId: string, role: 'source' | 'target') => void;
  onReturnToOverview?: () => void;
  // Pipeline target selection (overview mode)
  onSelectPipelineTarget?: (name: string | null) => void;
  pipelineSelectedTarget?: string | null;
  // Search (moved from toolbar)
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  // Navigation back to Pipeline DAG
  onBackToPipeline?: () => void;
  // Issue #656: unified Review pane data + selection.
  reviewItems: ReviewItem[];
  reviewCounts: ReviewItemCounts;
  activeFieldSelection: FieldSelection;
  onSelectReviewItem: (item: ReviewItem) => void;
  /** Optional initial Review-pane tab (e.g., from drift-banner Broken click). */
  initialReviewTab?: ReviewBucketTab;
  /** Decision-kind preload counts (validator_summary.by_kind). */
  decisionKindFallbackCounts?: Record<string, number>;
  /** Plan §J: Esc-to-clear from inside the Review pane. */
  onClearReviewSelection?: () => void;
  /** Plan §I: surface a tab-specific error state when decisions fetch fails. */
  decisionsError?: boolean;
  /** Readiness rollout — threaded into ReviewPane so the Readiness
   *  banner above the legacy review list can fetch /readiness with
   *  the active session and surface the same findings the toolbar
   *  chip is using. Optional so callers that don't have a session yet
   *  (legacy tests / mapping-without-pipeline) still mount cleanly. */
  sessionId?: string;
}

// ─── Section header helper (used only by Tables Navigator now) ──────────
const SectionHeader: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  badge?: React.ReactNode;
}> = ({ title, open, onToggle, badge }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      py: 1,
      px: 0.5,
      borderRadius: 1,
      '&:hover': { backgroundColor: 'action.hover' },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography
        variant="subtitle2"
        fontWeight={600}
        fontSize={12}
        textTransform="uppercase"
        color="text.secondary"
      >
        {title}
      </Typography>
      {badge}
    </Stack>
    <Typography variant="caption" color="text.secondary" fontSize={11}>
      {open ? '▾' : '▸'}
    </Typography>
  </Box>
);

// ─── Component ──────────────────────────────────────────────────────────
const MappingOverviewPanel: React.FC<MappingOverviewPanelProps> = ({
  mappingResponse,
  canvasGraph,
  focusedTargetIds,
  focusedSourceIds,
  onToggleTarget,
  onToggleSource,
  onClearFocus,
  availableGroups,
  onFocusNode,
  onSelectEdge,
  canvasViewMode = 'canvas',
  canvasSubState = 'overview',
  detailTableId,
  detailTableRole,
  onDrillIntoTable,
  onReturnToOverview,
  onSelectPipelineTarget,
  pipelineSelectedTarget,
  searchQuery = '',
  onSearchQueryChange,
  onBackToPipeline,
  reviewItems,
  reviewCounts,
  activeFieldSelection,
  onSelectReviewItem,
  initialReviewTab,
  decisionKindFallbackCounts,
  onClearReviewSelection,
  decisionsError,
  sessionId,
}) => {
  // Issue #656 visual revision: top-level tabs make Tables and Review
  // peers — operator picks where they're focused, each gets the full
  // vertical real estate. Default is `tables` so navigation isn't
  // buried under a 90-item warning list.
  const [topPaneTab, setTopPaneTab] = React.useState<'tables' | 'review'>('tables');
  // Tables Navigator is always-open inside its tab now (no nested collapse).
  const tablesOpen = true;
  const setTablesOpen = (_: any) => {};
  // When the drift banner activates a Review filter (initialReviewTab),
  // auto-switch to the Review tab so the filter is visible.
  React.useEffect(() => {
    if (initialReviewTab) setTopPaneTab('review');
  }, [initialReviewTab]);
  // ─── Summary stats (still computed for the Tables-Navigator average
  // confidence colouring; SUMMARY block was removed by Issue #656). ──
  const stats = useMemo(() => {
    const mappings = mappingResponse?.mappings ?? [];
    const nodes = canvasGraph?.nodes ?? [];

    const targetFieldNodes = nodes.filter(
      (n) => n.data.kind === 'targetField',
    );

    let totalTargetFields: number;
    let mappedCount: number;

    if (targetFieldNodes.length > 0) {
      // Field view: use canvas graph nodes
      const unmappedNodes = targetFieldNodes.filter((n) => n.data.isUnmapped);
      totalTargetFields = targetFieldNodes.length;
      mappedCount = totalTargetFields - unmappedNodes.length;
    } else {
      // Table view or filtered: compute from mappingResponse + schema snapshot
      const meta = mappingResponse?.metadata;
      const targetSnapshot =
        (meta?.target_schema_snapshot?.tables ??
         meta?.target_schema_snapshot?.tables ??
         meta?.schema_context?.target_schema_snapshot?.tables ??
         mappingResponse?.transformation_plan?.target_schema_snapshot?.tables) as
          Record<string, { columns?: Array<{ name: string }> }> | undefined;

      // Count total target fields from schema snapshot
      if (targetSnapshot) {
        totalTargetFields = Object.values(targetSnapshot).reduce(
          (sum, t) => sum + (t.columns?.length ?? 0), 0
        );
      } else {
        // Fallback: count unique target fields from mappings
        const uniqueTargetFields = new Set(mappings.map((m) => `${m.target_table}.${m.target_field}`));
        totalTargetFields = uniqueTargetFields.size;
      }
      const uniqueMapped = new Set(mappings.map((m) => `${m.target_table}.${m.target_field}`));
      mappedCount = uniqueMapped.size;
    }

    let highCount = 0;
    let medCount = 0;
    let lowCount = 0;
    let aiCount = 0;
    let manualCount = 0;

    for (const m of mappings) {
      if (m.confidence >= CONF_HIGH) highCount++;
      else if (m.confidence >= CONF_MED) medCount++;
      else lowCount++;

      const steps = m.transformation_steps ?? [];
      for (const s of steps) {
        if (s.ai_generated) aiCount++;
        else manualCount++;
      }
    }
    // If no steps tracked, fall back to mapping count as AI
    if (aiCount === 0 && manualCount === 0) {
      aiCount = mappings.length;
    }

    return {
      totalMappings: mappings.length,
      totalTargetFields,
      mappedCount,
      unmappedCount: totalTargetFields - mappedCount,
      highCount,
      medCount,
      lowCount,
      aiCount,
      manualCount,
      overallConfidence: mappingResponse?.ai_confidence_score ?? 0,
    };
  }, [mappingResponse, canvasGraph]);

  // ─── Table-level stats ──────────────────────────────────────────────
  // Compute from mappingResponse directly so stats are correct in both
  // field view and table view (table view graph has no field-level nodes).
  const tableStats = useMemo<TableStat[]>(() => {
    const mappings = mappingResponse?.mappings ?? [];
    const nodes = canvasGraph?.nodes ?? [];

    // Extract target schema snapshot to get total field counts per target table
    const meta = mappingResponse?.metadata;
    const targetSnapshot =
      (meta?.target_schema_snapshot?.tables ??
       meta?.target_schema_snapshot?.tables ??
       meta?.schema_context?.target_schema_snapshot?.tables ??
       mappingResponse?.transformation_plan?.target_schema_snapshot?.tables) as
        Record<string, { columns?: Array<{ name: string }> }> | undefined;

    // Build a case-insensitive lookup for target schema column counts
    const targetSchemaFieldCounts = new Map<string, number>();
    if (targetSnapshot) {
      for (const [key, table] of Object.entries(targetSnapshot)) {
        targetSchemaFieldCounts.set(key.toLowerCase(), table.columns?.length ?? 0);
      }
    }

    return availableGroups.map((g) => {
      // First try canvas graph nodes (works in field view)
      const fieldNodes = nodes.filter(
        (n) =>
          (g.role === 'target' ? n.data.kind === 'targetField' : n.data.kind === 'sourceField') &&
          n.data.table === g.label,
      );

      let total: number;
      let mapped: number;

      if (fieldNodes.length > 0) {
        // Field view: use canvas graph nodes directly
        const unmapped = fieldNodes.filter((n) => n.data.isUnmapped).length;
        total = fieldNodes.length;
        mapped = total - unmapped;
      } else {
        // Table view or filtered: compute from mappingResponse
        const tableMappingsForCount =
          g.role === 'target'
            ? mappings.filter((m) => m.target_table === g.label)
            : mappings.filter((m) => m.source_file === g.label);

        if (g.role === 'target') {
          // For target: total from schema snapshot, mapped from unique target fields in mappings
          const schemaTotal = targetSchemaFieldCounts.get(g.label.toLowerCase());
          const uniqueMappedFields = new Set(tableMappingsForCount.map((m) => m.target_field));
          mapped = uniqueMappedFields.size;
          total = schemaTotal ?? mapped; // fall back to mapped count if no schema
        } else {
          // For source: count unique source fields
          const uniqueFields = new Set(tableMappingsForCount.map((m) => m.source_field));
          mapped = uniqueFields.size;
          total = mapped; // source total = what we know about
        }
      }

      // Avg confidence for this table
      const tableMappings =
        g.role === 'target'
          ? mappings.filter((m) => m.target_table === g.label)
          : mappings.filter((m) => m.source_file === g.label);
      const avgConf =
        tableMappings.length > 0
          ? tableMappings.reduce((sum, m) => sum + m.confidence, 0) / tableMappings.length
          : 0;

      return {
        id: g.id,
        label: g.label,
        role: g.role,
        totalFields: total,
        mappedFields: mapped,
        unmappedFields: total - mapped,
        avgConfidence: avgConf,
      };
    });
  }, [mappingResponse, canvasGraph, availableGroups]);

  // Issue #656: review-queue derivation, navigation helpers, and
  // confidence-bucket counts have moved to `useReviewItems` upstream.
  // The Review pane consumes items + counts via props.

  // ─── Table focus click ──────────────────────────────────────────────
  const handleTableClick = useCallback(
    (tableStat: TableStat) => {
      // In mapping-table mode or canvas overview, toggle multi-select filter
      if (canvasViewMode === 'mapping-table' || canvasSubState === 'overview') {
        if (tableStat.role === 'target') {
          onToggleTarget(tableStat.id);
        } else {
          onToggleSource(tableStat.id);
        }
        return;
      }
      // In canvas detail mode, drill into the clicked table
      if (onDrillIntoTable) {
        onDrillIntoTable(tableStat.label, tableStat.role);
        return;
      }
      // Fallback: focus the canvas node
      const nodes = canvasGraph?.nodes ?? [];
      const groupNode = nodes.find(
        (n) =>
          (n.data.kind === 'tableGroup' || n.data.kind === 'tableNode') &&
          (n.data.label === tableStat.label || n.data.table === tableStat.label),
      );
      if (groupNode) {
        onFocusNode(groupNode.id);
      }
    },
    [canvasGraph, canvasViewMode, onFocusNode, canvasSubState, onToggleTarget, onToggleSource, onDrillIntoTable],
  );

  // ─── Search filter ─────────────────────────────────────────────────
  const lowerSearch = searchQuery.toLowerCase().trim();

  // Filter table stats by search query — also matches field names within mappings
  const filteredTableStats = useMemo(() => {
    if (!lowerSearch) return tableStats;
    const mappings = mappingResponse?.mappings ?? [];
    return tableStats.filter((t) => {
      // Match table name
      if (t.label.toLowerCase().includes(lowerSearch)) return true;
      // Match any field name within this table's mappings
      const tableMappings = t.role === 'target'
        ? mappings.filter((m) => m.target_table === t.label)
        : mappings.filter((m) => m.source_file === t.label);
      return tableMappings.some(
        (m) =>
          m.target_field?.toLowerCase().includes(lowerSearch) ||
          m.source_field?.toLowerCase().includes(lowerSearch),
      );
    });
  }, [tableStats, lowerSearch, mappingResponse]);

  // ─── Render ─────────────────────────────────────────────────────────
  const targets = filteredTableStats.filter((t) => t.role === 'target');
  const sources = filteredTableStats.filter((t) => t.role === 'source');

  const isDetailMode = canvasSubState === 'detail';

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexShrink: 0,
        }}
      >
        {isDetailMode && onReturnToOverview ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton size="small" onClick={onReturnToOverview} sx={{ mr: 0.25 }}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontSize={10} lineHeight={1.2}>
                {detailTableRole === 'source' ? 'Source' : 'Target'} Detail
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} fontSize={12} noWrap lineHeight={1.2}>
                {detailTableId ?? 'Table'}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} fontSize={13}>
              Navigator
            </Typography>
            {onBackToPipeline && (
              <Button
                size="small"
                variant="outlined"
                onClick={onBackToPipeline}
                startIcon={<PipelineIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: 10,
                  textTransform: 'none',
                  py: 0.25,
                  px: 1,
                  borderRadius: 1.5,
                  height: 24,
                  borderColor: 'text.secondary',
                  borderWidth: 1.5,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'text.primary',
                    color: 'text.primary',
                  },
                }}
              >
                Back to Pipeline
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {/* Search Bar */}
      {onSearchQueryChange && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder="Search tables & fields..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: 12,
                borderRadius: 1.5,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      <Box sx={{ overflowY: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Issue #656 visual revision: top-level tabs split Tables vs
        Review so neither gets buried. Tables is the default — it's the
        navigation primitive operators reach for first; Review (often
        an overwhelming long list) is one click away. Drift-banner
        Broken/Stale clicks auto-switch to Review (see effect below). */}
        <Tabs
          value={topPaneTab}
          onChange={(_, v) => setTopPaneTab(v as 'tables' | 'review')}
          variant="fullWidth"
          sx={{
            minHeight: 32,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0,
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          <Tab
            value="tables"
            label={`Tables ${availableGroups.length}`}
          />
          <Tab
            value="review"
            label={`Review ${reviewCounts.all}`}
            sx={{
              color: reviewCounts.all > 0 ? 'warning.main' : undefined,
            }}
          />
        </Tabs>

        {topPaneTab === 'review' && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ReviewPane
              items={reviewItems}
              counts={reviewCounts}
              activeSelection={activeFieldSelection}
              onSelect={onSelectReviewItem}
              initialTab={initialReviewTab}
              searchQuery={searchQuery}
              tableFilter={isDetailMode && detailTableId ? detailTableId : null}
              onClearTableFilter={isDetailMode ? onReturnToOverview : undefined}
              decisionKindFallbackCounts={decisionKindFallbackCounts as any}
              onClearSelection={onClearReviewSelection}
              decisionsError={decisionsError}
              sessionId={sessionId}
            />
          </Box>
        )}

        {topPaneTab === 'tables' && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1 }}>

        {(focusedTargetIds.size + focusedSourceIds.size > 0) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <Typography
              variant="caption"
              onClick={onClearFocus}
              sx={{ color: 'primary.main', cursor: 'pointer', fontSize: 11 }}
            >
              {focusedTargetIds.size + focusedSourceIds.size} focused · clear
            </Typography>
          </Box>
        )}
        <Collapse in={tablesOpen}>
          <Box sx={{ pb: 1 }}>
            {/* Targets */}
            {targets.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} textTransform="uppercase" color="text.secondary" sx={{ px: 0.5 }}>
                  Target ({targets.length})
                </Typography>
                {targets.map((t) => {
                  const isSelected = focusedTargetIds.has(t.id);
                  const isDrillTarget = isDetailMode && detailTableId === t.label && detailTableRole === 'target';
                  return (
                    <Box
                      key={t.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 0.75,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isDrillTarget ? 'primary.50' : isSelected ? '#ecfdf5' : 'transparent',
                        border: '1px solid',
                        borderColor: isDrillTarget ? 'primary.main' : isSelected ? '#22c55e' : 'transparent',
                        '&:hover': { bgcolor: isDrillTarget ? 'primary.100' : 'action.hover' },
                        mt: 0.5,
                      }}
                    >
                      {/* Eye icon for drill-in/out */}
                      {onDrillIntoTable && (
                        <Tooltip title={isDrillTarget ? 'Back to overview' : 'View field details'} arrow placement="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isDrillTarget && onReturnToOverview) {
                                onReturnToOverview();
                              } else {
                                onDrillIntoTable(t.label, 'target');
                              }
                            }}
                            sx={{
                              p: 0.25,
                              color: isDrillTarget ? 'primary.main' : 'text.secondary',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {isDrillTarget ? (
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Box
                        sx={{ flex: 1, minWidth: 0 }}
                        onClick={() => handleTableClick(t)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Typography variant="body2" fontWeight={isDrillTarget || isSelected ? 700 : 400} fontSize={11} noWrap>
                            {t.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontSize={9} sx={{ flexShrink: 0, ml: 0.5 }}>
                            {t.mappedFields}/{t.totalFields}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={t.totalFields > 0 ? (t.mappedFields / t.totalFields) * 100 : 0}
                          sx={{
                            height: 3,
                            borderRadius: 1.5,
                            mt: 0.25,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: confidenceColor(t.avgConfidence),
                              borderRadius: 1.5,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {targets.length > 0 && sources.length > 0 && <Divider sx={{ my: 0.5 }} />}

            {/* Sources */}
            {sources.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} textTransform="uppercase" color="text.secondary" sx={{ px: 0.5 }}>
                  Source ({sources.length})
                </Typography>
                {sources.map((t) => {
                  const isSelected = focusedSourceIds.has(t.id);
                  const isDrillTarget = isDetailMode && detailTableId === t.label && detailTableRole === 'source';
                  return (
                    <Box
                      key={t.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 0.75,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isDrillTarget ? 'primary.50' : isSelected ? '#eff6ff' : 'transparent',
                        border: '1px solid',
                        borderColor: isDrillTarget ? 'primary.main' : isSelected ? '#3b82f6' : 'transparent',
                        '&:hover': { bgcolor: isDrillTarget ? 'primary.100' : 'action.hover' },
                        mt: 0.5,
                      }}
                    >
                      {/* Eye icon for drill-in/out */}
                      {onDrillIntoTable && (
                        <Tooltip title={isDrillTarget ? 'Back to overview' : 'View field details'} arrow placement="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isDrillTarget && onReturnToOverview) {
                                onReturnToOverview();
                              } else {
                                onDrillIntoTable(t.label, 'source');
                              }
                            }}
                            sx={{
                              p: 0.25,
                              color: isDrillTarget ? 'primary.main' : 'text.secondary',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {isDrillTarget ? (
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => handleTableClick(t)}>
                        <Typography variant="body2" fontWeight={isDrillTarget || isSelected ? 700 : 400} fontSize={11} noWrap>
                          {t.label}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Collapse>

        </Box>
        )}
      </Box>

      {/* Footer hint */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexShrink: 0,
        }}
      >
        {!isDetailMode && (focusedTargetIds.size + focusedSourceIds.size) > 0 ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              {focusedTargetIds.size + focusedSourceIds.size} table{focusedTargetIds.size + focusedSourceIds.size !== 1 ? 's' : ''} selected
            </Typography>
            <Typography
              variant="caption"
              fontSize={10}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={onClearFocus}
            >
              Clear selection
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled" fontSize={10}>
            {isDetailMode ? 'Press Escape to return to overview.' : 'Click tables to filter the pipeline view.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(MappingOverviewPanel);
